import { useQueryClient } from '@tanstack/react-query';
import { persistQueryClient } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { useEffect } from 'react';

export interface PersistOptions {
  /**
   * Tipo de storage a usar
   * @default 'localStorage'
   */
  storageType?: 'localStorage' | 'sessionStorage';
  
  /**
   * Clave única para el storage
   * @default 'fianza-inteligente-cache'
   */
  key?: string;
  
  /**
   * Tiempo máximo que se mantiene el cache (en ms)
   * @default 24 horas
   */
  maxAge?: number;
  
  /**
   * Tiempo de throttle para guardar (en ms)
   * @default 1000
   */
  throttleTime?: number;
  
  /**
   * Habilitar logs de depuración
   * @default false
   */
  debug?: boolean;
}

export function usePersistQueryClient(options: PersistOptions = {}) {
  const queryClient = useQueryClient();
  
  const {
    storageType = 'localStorage',
    key = 'fianza-inteligente-cache',
    maxAge = 1000 * 60 * 60 * 24, // 24 horas
    throttleTime = 1000,
    debug = false,
  } = options;

  useEffect(() => {
    // Solo ejecutar en el cliente
    if (typeof window === 'undefined') {
      return;
    }

    try {
      const storage = storageType === 'sessionStorage' 
        ? window.sessionStorage 
        : window.localStorage;

      const persister = createSyncStoragePersister({
        storage,
        key,
        throttleTime,
        serialize: (data) => {
          try {
            return JSON.stringify(data);
          } catch (error) {
            if (debug) console.error('Error serializando cache:', error);
            return '';
          }
        },
        deserialize: (data) => {
          try {
            return JSON.parse(data);
          } catch (error) {
            if (debug) console.error('Error deserializando cache:', error);
            return {};
          }
        },
      });

      persistQueryClient({
        queryClient,
        persister,
        maxAge,
        dehydrateOptions: {
          // Solo persistir queries exitosas
          shouldDehydrateQuery: (query) => query.state.status === 'success',
        },
      });

      if (debug) {
        console.log(`✅ Cache persistido configurado:`, {
          storage: storageType,
          key,
          maxAge: `${maxAge / 1000}s`,
          throttleTime: `${throttleTime}ms`,
        });
      }

    } catch (error) {
      console.error('Error configurando persistencia de cache:', error);
    }
  }, [queryClient, storageType, key, maxAge, throttleTime, debug]);
}

export function clearPersistedCache(
  key: string = 'fianza-inteligente-cache',
  storageType: 'localStorage' | 'sessionStorage' = 'localStorage'
) {
  if (typeof window === 'undefined') return;
  
  try {
    const storage = storageType === 'sessionStorage' 
      ? window.sessionStorage 
      : window.localStorage;
    
    storage.removeItem(key);
    console.log(`🗑️ Cache persistido limpiado (${storageType}:${key})`);
  } catch (error) {
    console.error('Error limpiando cache persistido:', error);
  }
} 